import { DateTime } from 'luxon'
import { adminFetchSubscriptionsBySubscriptionId } from '@/actions/adminActions'
import { UserWithAdminInfo } from '@/reducers/admin/allUsersReducer'
import { selectPrimaryMembershipForUser } from '@/selectors/user.selectors'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { getBillingPeriod } from '@/utils/stripeSubscriptionHelpers'
import { useFetchResponse, useReselector } from '@/utils/sharedHooks'
import { Alert } from '@/ui'

const CancellingAlert = ({ periodEnd }: { periodEnd?: number }) => {
  const endDate = periodEnd
    ? DateTime.fromSeconds(periodEnd).toFormat(DATE_FORMATS_LUXON.DISPLAY_SHORT)
    : 'N/A'

  return (
    <Alert variant="warning">
      <b>Please continue servicing this user.</b>
      <br />
      This user is cancelling their plan, but still has service days left in
      their billing cycle.
      <br />
      <b>Subscription Ends On: {endDate}</b>
    </Alert>
  )
}

const CancellingAlertWithSubscription = ({
  userId,
  subscriptionId,
}: {
  userId?: number
  subscriptionId: string
}) => {
  const subscription = useFetchResponse(
    adminFetchSubscriptionsBySubscriptionId,
    userId,
    subscriptionId
  )

  const periodEnd = subscription
    ? getBillingPeriod(subscription, subscription.primaryPriceId).end
    : null

  return <CancellingAlert periodEnd={periodEnd ?? undefined} />
}

export const UserAccountCanceledAlert = ({
  user,
}: {
  user?: UserWithAdminInfo
}) => {
  const membership = useReselector(selectPrimaryMembershipForUser, user?.id)

  if (membership?.status === 'cancelling') {
    if (!membership.stripeSubscription) {
      return <CancellingAlert />
    }
    return (
      <CancellingAlertWithSubscription
        userId={user?.id}
        subscriptionId={membership.stripeSubscription}
      />
    )
  } else if (
    membership?.status &&
    !['trial', 'paid'].includes(membership?.status)
  ) {
    return (
      <Alert variant="error">
        <b>Do Not Service this User</b>
        <br />
        This User is not a paying User
      </Alert>
    )
  } else return null
}
